import { Component, OnInit, Input, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { matchPassword } from '../../../shared/validation/validation.helpers';
import { UsersService } from '../users.service';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from 'src/app/toast/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subscription } from 'rxjs';

@Component({
  selector: 'app-user-change-password',
  templateUrl: './change-password.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangePasswordComponent implements OnInit, OnDestroy {
  @Input()
  public modalReference: NgbModalRef;

  public passwordType: BehaviorSubject<string> = new BehaviorSubject<string>('password');
  public confirmationPasswordType: BehaviorSubject<string> = new BehaviorSubject<string>('password');
  public userForm: FormGroup;
  public readonly MIN_LENGTH = 3;
  
  private unsubscribe: Subscription[] = [];

  constructor(
    private readonly usersService: UsersService,
    private readonly toastService: ToastService,
    private readonly translateService: TranslateService,
  ) {
  }

  public get showPasswordIconClass() {
    return this.passwordType.getValue() === 'text' ? 'bi-eye-fill' : 'bi-eye-slash-fill';
  }

  public get showConfirmationPasswordIconClass() {
    return this.confirmationPasswordType.getValue() === 'text' ? 'bi-eye-fill' : 'bi-eye-slash-fill';
  }

  public ngOnInit(): void {
    this.createForm();
  }

  public createForm() {
    this.userForm = new FormGroup({
      newPassword: new FormControl('', [Validators.required, Validators.minLength(this.MIN_LENGTH)]),
      newPasswordConfirmation: new FormControl('', [Validators.required, Validators.minLength(this.MIN_LENGTH)]),
    }, {
      validators: matchPassword('newPassword', 'newPasswordConfirmation')
    });
  }

  public isTouchedAndHasError(control: string, errorCodes: string[]) {
    if (!this.userForm.get(control)?.touched) {
      return false;
    }

    return this.hasError(control, errorCodes);
  }

  public onSaveClick() {
    const changePasswordSubscription = this.usersService.changePassword(this.userForm.get('newPassword')?.value)
      .subscribe(
        () => {
          this.toastService.success(
            this.translateService.instant('USERS.CHANGE_PASSWORD.SUCCESS')
          );

          this.closeModal();
        },
        () => {
          this.toastService.error(
            this.translateService.instant('USERS.CHANGE_PASSWORD.FAIL')
          );
        }
      );

    this.unsubscribe.push(changePasswordSubscription);
  }

  public closeModal() {
    this.modalReference.close();
  }

  public ngOnDestroy() {
    this.unsubscribe.forEach(sb => sb.unsubscribe());
  }

  public onChangeConfirmationPasswordType(fieldType: string) {
    this.confirmationPasswordType.next(fieldType);
  }

  public onChangePasswordType(fieldType: string) {
    this.passwordType.next(fieldType);
  }

  private hasError(control: string, errorCodes: string[]): boolean {
    for (const errorCode of errorCodes) {
      if (this.userForm.get(control)?.hasError(errorCode)) {
        return true;
      }
    }

    return false;
  }
}
